import {
  Box,
  BoxProps,
  Divider,
  Heading,
  Image,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { sponsor_content_clicked, sponsor_link_clicked } from '@/analytics';
import { IRequestDemoView } from '@/types/analytics';
import { ImageFallback } from '@/imports/ui/chakra/components/ImageFallback';
import { RequestDemoButton } from '@/imports/ui/components/RequestDemoButton';
import { getExternalUrl } from '@/utilities/getExternalUrl';

export const EventPromotionBox: React.FC<
  {
    image?: string;
    organizationName: string;
    organizationId: string;
    title: string;
    body: string;
    callToAction: string;
    url: string;
    registerInterestTitle: string | null;
    origin: {
      conferenceBoothId?: string;
      sponsoredEventId?: string;
      sponsoredConferenceSessionId?: string;
      hostedEventId?: string;
      sponsorPage?: boolean;
    };
    analytics: {
      view: IRequestDemoView;
      tier: string;
    };
  } & BoxProps
> = ({
  image,
  organizationName,
  organizationId,
  title,
  body,
  callToAction,
  url,
  registerInterestTitle,
  origin,
  analytics,
  ...boxProps
}) => {
  const fullUrl = getExternalUrl(url);

  // Track sponsor content click (when user clicks on sponsor name/image)
  const handleSponsorContentClick = () => {
    sponsor_content_clicked({
      entity: {
        id: organizationId,
        type: 'sponsor_content',
      },
      sponsor: {
        id: organizationId,
        name: organizationName,
      },
      content: {
        contentType: 'booth', // This is a sponsor booth/promotion
        contentId: organizationId,
      },
      sponsor_id: organizationId,
      sponsor_name: organizationName,
      sponsor_tier: analytics.tier,
      sponsor_interaction_source: 'event',
      event_backed_sponsor_interaction: true,
      event_sponsor_id: organizationId,
      event_id: origin.sponsoredEventId || origin.hostedEventId,
      event_session_id:
        origin.conferenceBoothId || origin.sponsoredConferenceSessionId,
      is_sponsor_interaction: true,
    });
  };

  // Track external link click
  const handleExternalLinkClick = () => {
    sponsor_link_clicked({
      entity: {
        id: organizationId,
        type: 'sponsor_link',
      },
      sponsor: {
        id: organizationId,
        name: organizationName,
      },
      navigation: {
        linkType: 'website', // External website link
        destinationUrl: fullUrl || '',
      },
      sponsor_id: organizationId,
      sponsor_name: organizationName,
      sponsor_tier: analytics.tier,
      sponsor_interaction_source: 'event',
      event_backed_sponsor_interaction: true,
      event_sponsor_id: organizationId,
      event_id: origin.sponsoredEventId || origin.hostedEventId,
      event_session_id:
        origin.conferenceBoothId || origin.sponsoredConferenceSessionId,
      is_sponsor_interaction: true,
    });
  };

  return (
    <Box w="280px" textAlign="center" {...boxProps}>
      <Stack spacing="4">
        <NextLink href={`/companies/${organizationId}`}>
          <Stack spacing="4" cursor="pointer" onClick={handleSponsorContentClick}>
            <Image
              alt={`Sponsor promotion for ${organizationName}`}
              src={image}
              fallback={<ImageFallback aspectRatio="1" borderRadius="base" />}
              mx="auto"
              borderRadius="base"
            />

            <Heading size="md">{organizationName}</Heading>
          </Stack>
        </NextLink>

        <Divider my="6" />

        <Stack spacing="6">
          <Stack spacing="1">
            <Heading size="sm">{title}</Heading>
            <Text color="gray.600">{body}</Text>
          </Stack>
          <RequestDemoButton
            options={{
              organizationId,
              origin,
              registerInterestTitle,
              analytics,
            }}
            w="full"
          />
        </Stack>

        <Link href={fullUrl} isExternal onClick={handleExternalLinkClick}>
          {callToAction}
        </Link>
      </Stack>
    </Box>
  );
};
